  import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
    useCallback,
  } from "react";
  import axios from "axios";
  import {
    Box,
    Button,
    TextField,
    Snackbar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    Chip,
    Stack,
    Typography,
    TableSortLabel,
    TablePagination,
    Autocomplete,
    InputAdornment,
    Collapse,
    Divider,
    Card,
    CardContent,
    Fade,
    Skeleton,
    Badge,
    alpha,
    useTheme,
    styled,
    keyframes,
  } from "@mui/material";
  import DeleteIcon from "@mui/icons-material/Delete";
  import EditIcon from "@mui/icons-material/Edit";
  import RefreshIcon from "@mui/icons-material/Refresh";
  import AddIcon from "@mui/icons-material/Add";
  import FileDownloadIcon from "@mui/icons-material/FileDownload";
  import ContentCopyIcon from "@mui/icons-material/ContentCopy";
  import OpenInNewIcon from "@mui/icons-material/OpenInNew";
  import SearchIcon from "@mui/icons-material/Search";
  import ClearIcon from "@mui/icons-material/Clear";
  import FilterAltIcon from "@mui/icons-material/FilterAlt";
  import ExpandLessIcon from "@mui/icons-material/ExpandLess";
  import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
  import { useNavigate } from "react-router-dom";
  import { baseUrl } from "../../constants";
  import AccountFormDialog from "./AccountFormDialog";
  import { getUserInfoFromToken } from "../../utils/Main";
  import BusinessIcon from "@mui/icons-material/Business";
  import AccountCircleIcon from "@mui/icons-material/AccountCircle";

  interface Account {
    id: number;
    name: string;
    slug: string;

    description?: string;
    notes?: string;

    type: string;
    status: string;
    visibility: string;

    tags: string[];

    email?: string;
    phone?: string;
    website?: string;
    domain?: string;

    address?: string;
    city?: string;
    country?: string;

    workspaceId: number;
    linkedUserId?: number;

    createdAt: string;
    updatedAt: string;
  }

  type Order = "asc" | "desc";

  const TYPE_OPTIONS = [
    "ALL",
    "ORGANIZATION",
    "PERSON",
    "PARTNER",
    "VENDOR",
    "INTERNAL",
  ] as const;

  const STATUS_OPTIONS = [
    "ALL",
    "ACTIVE",
    "PROSPECT",
    "DORMANT",
    "CHURNED",
    "ARCHIVED",
  ] as const;

  const VISIBILITY_OPTIONS = ["ALL", "WORKSPACE", "DEPARTMENT"] as const;

  const DEFAULT_ROWS_PER_PAGE = 20;

  const tokenHeader = () => {
    const t =
      localStorage.getItem("accessToken") || localStorage.getItem("token");
    return { Authorization: `Bearer ${t}` };
  };

  const storageKey = "accounts_filters_v2";

  /* ------------------------------------------------------------------ */
  /* Styled Components & Animations                                     */
  /* ------------------------------------------------------------------ */

  const fadeIn = keyframes`
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `;

  const GlassCard = styled(Paper)(({ theme }) => ({
    background:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.background.paper, 0.85)
        : alpha("#ffffff", 0.95),
    backdropFilter: "saturate(180%) blur(20px)",
    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    borderRadius: 16,
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 8px 32px rgba(0, 0, 0, 0.4)"
        : "0 8px 32px rgba(0, 0, 0, 0.08)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    animation: `${fadeIn} 0.4s ease-out`,
    "&:hover": {
      boxShadow:
        theme.palette.mode === "dark"
          ? "0 12px 48px rgba(0, 0, 0, 0.5)"
          : "0 12px 48px rgba(0, 0, 0, 0.12)",
    },
  }));

  const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 600,
    padding: "10px 24px",
    boxShadow: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow:
        theme.palette.mode === "dark"
          ? "0 8px 24px rgba(0, 0, 0, 0.4)"
          : "0 8px 24px rgba(0, 0, 0, 0.15)",
    },
  }));

  const StyledIconButton = styled(IconButton)(({ theme }) => ({
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "scale(1.1)",
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
  }));

  const EmptyState = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6),
    textAlign: "center",
    color: theme.palette.text.secondary,
    minHeight: 400,
  }));

  const Accounts: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    const user = getUserInfoFromToken();
    const workspaceId = user?.workspaceId;

    // table data + server paging/sorting
    const [rows, setRows] = useState<Account[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);

    // filters
    const [searchText, setSearchText] = useState("");
    const [filterType, setFilterType] =
      useState<(typeof TYPE_OPTIONS)[number]>("ALL");
    const [filterStatus, setFilterStatus] =
      useState<(typeof STATUS_OPTIONS)[number]>("ALL");
    const [filterVisibility, setFilterVisibility] =
      useState<(typeof VISIBILITY_OPTIONS)[number]>("ALL");
    const [filterTags, setFilterTags] = useState<string[]>([]);

    // UI bits
    const [filtersOpen, setFiltersOpen] = useState(false);

    // tags suggestions from loaded rows
    const tagOptions = useMemo(() => {
      const s = new Set<string>();
      rows.forEach((r) => (r.tags || []).forEach((t) => s.add(t)));
      return Array.from(s).sort((a, b) => a.localeCompare(b));
    }, [rows]);

    // sorting/pagination
    const [orderBy, setOrderBy] = useState<keyof Account>("name");
    const [order, setOrder] = useState<Order>("asc");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

    // dialogs
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editAccount, setEditAccount] = useState<Account | null>(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteAccountId, setDeleteAccountId] = useState<number | null>(null);

    const [snackbar, setSnackbar] = useState<{
      open: boolean;
      message: string;
      severity: "success" | "error" | "info";
    }>({ open: false, message: "", severity: "success" });

    // Persist filters to localStorage
    useEffect(() => {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          const p = JSON.parse(saved);
          setSearchText(p.searchText ?? "");
          setFilterType(p.filterType ?? "ALL");
          setFilterStatus(p.filterStatus ?? "ALL");
          setFilterVisibility(p.filterVisibility ?? "ALL");
          setFilterTags(Array.isArray(p.filterTags) ? p.filterTags : []);
          setOrderBy(p.orderBy ?? "name");
          setOrder(p.order ?? "asc");
          setRowsPerPage(p.rowsPerPage ?? DEFAULT_ROWS_PER_PAGE);
          setFiltersOpen(Boolean(p.filtersOpen));
        } catch {}
      }
    }, []);

    useEffect(() => {
      const payload = {
        searchText,
        filterType,
        filterStatus,
        filterVisibility,
        filterTags,
        orderBy,
        order,
        rowsPerPage,
        filtersOpen,
      };
      localStorage.setItem(storageKey, JSON.stringify(payload));
    }, [
      searchText,
      filterType,
      filterStatus,
      filterVisibility,
      filterTags,
      orderBy,
      order,
      rowsPerPage,
      filtersOpen,
    ]);

    const debounceRef = useRef<number | undefined>(undefined);
    const [debouncedQ, setDebouncedQ] = useState("");

    useEffect(() => {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(
        () => setDebouncedQ(searchText.trim()),
        300
      );
      return () => window.clearTimeout(debounceRef.current);
    }, [searchText]);

    const fetchAccounts = useCallback(async () => {
      try {
        setLoading(true);
        const url = `${baseUrl}/accounts/workspace/query`;

        const params: any = {
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedQ || undefined,
          type: filterType !== "ALL" ? filterType : undefined,
          status: filterStatus !== "ALL" ? filterStatus : undefined,
          visibility: filterVisibility !== "ALL" ? filterVisibility : undefined,
          tags: filterTags.length ? filterTags.join(",") : undefined,
          orderBy,
          order,
        };

        const { data } = await axios.get<{ items: Account[]; total: number }>(
          url,
          {
            headers: tokenHeader(),
            params,
          }
        );

        setRows(data?.items ?? []);
        setTotal(data?.total ?? 0);
      } catch (err) {
        console.error("Failed to fetch accounts", err);
        setSnackbar({
          open: true,
          message: "Failed to load accounts",
          severity: "error",
        });
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    }, [
      debouncedQ,
      filterType,
      filterStatus,
      filterVisibility,
      filterTags,
      orderBy,
      order,
      page,
      rowsPerPage,
    ]);

    useEffect(() => {
      const token =
        localStorage.getItem("accessToken") || localStorage.getItem("token");
      if (!user || !token) {
        navigate("/");
        return;
      }
      fetchAccounts();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchAccounts]);

    const resetFilters = () => {
      setSearchText("");
      setFilterType("ALL");
      setFilterStatus("ALL");
      setFilterVisibility("ALL");
      setFilterTags([]);
      setPage(0);
    };

    const handleRequestSort = (property: keyof Account) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    };

    const copyToClipboard = (text: string) => {
      if (!text) return;
      navigator.clipboard?.writeText(text);
      setSnackbar({ open: true, message: "Copied!", severity: "success" });
    };

    const exportAccountsExcel = async () => {
      try {
        if (!rows.length) {
          setSnackbar({
            open: true,
            message: "No accounts to export.",
            severity: "info",
          });
          return;
        }

        const headers = [
          "id",
          "name",
          "slug",
          "type",
          "status",
          "visibility",
          "tags",
          "email",
          "phone",
          "website",
          "domain",
          "address",
          "city",
          "country",
          "workspaceId",
          "linkedUserId",
          "createdAt",
          "updatedAt",
        ] as const;

        const data = rows.map((a) => ({
          id: a.id,
          name: a.name, // Arabic-safe
          slug: a.slug,
          type: a.type,
          status: a.status,
          visibility: a.visibility,
          tags: (a.tags || []).join(" | "),
          email: a.email || "",
          phone: a.phone || "",
          website: a.website || "",
          domain: a.domain || "",
          address: a.address || "",
          city: a.city || "",
          country: a.country || "",
          workspaceId: a.workspaceId,
          linkedUserId: a.linkedUserId ?? "",
          createdAt: a.createdAt,
          updatedAt: a.updatedAt,
        }));

        const mod: any = await import("xlsx");
        const XLSX = mod.default ?? mod;

        const ws = XLSX.utils.json_to_sheet(data, { header: headers as any });

        // Autosize columns so Arabic text is fully visible
        (ws as any)["!cols"] = headers.map((h) => {
          const maxLen = Math.max(
            String(h).length,
            ...data.map((r) => String((r as any)[h] ?? "").length)
          );
          return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Accounts");

        XLSX.writeFile(wb, "accounts.xlsx");

        setSnackbar({
          open: true,
          message: "Exported to Excel.",
          severity: "success",
        });
      } catch {
        setSnackbar({
          open: true,
          message: "Excel export failed.",
          severity: "error",
        });
      }
    };

    const onPageChange = (_: unknown, newPage: number) => setPage(newPage);
    const onRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(e.target.value, 10));
      setPage(0);
    };

    const openCreateDialog = () => setAddDialogOpen(true);

    const handleCreate = async (payload: Record<string, any>) => {
      try {
        await axios.post(`${baseUrl}/accounts`, payload, {
          headers: tokenHeader(),
        });
        setSnackbar({
          open: true,
          message: "Account created!",
          severity: "success",
        });
        setAddDialogOpen(false);
        setPage(0);
        fetchAccounts();
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to create account";
        setSnackbar({ open: true, message: msg, severity: "error" });
        throw err;
      }
    };

    const handleUpdate = async (id: number, payload: Record<string, any>) => {
      try {
        const {
          workspaceId: _omit,
          slug: _slug,
          id: _id,
          createdAt,
          updatedAt,
          ...clean
        } = payload;
        const { data } = await axios.patch<Account>(
          `${baseUrl}/accounts/${id}`,
          clean,
          {
            headers: tokenHeader(),
          }
        );
        setSnackbar({
          open: true,
          message: "Account updated!",
          severity: "success",
        });
        setRows((prev) => prev.map((a) => (a.id === data.id ? data : a)));
        setEditDialogOpen(false);
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to update account";
        setSnackbar({ open: true, message: msg, severity: "error" });
        throw err;
      }
    };

    const handleDelete = async (id: number) => {
      try {
        await axios.delete(`${baseUrl}/accounts/${id}`, {
          headers: tokenHeader(),
        });
        setSnackbar({
          open: true,
          message: "Account deleted!",
          severity: "success",
        });
        setRows((prev) => prev.filter((a) => a.id !== id));
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to delete account";
        setSnackbar({ open: true, message: msg, severity: "error" });
      } finally {
        setDeleteDialogOpen(false);
      }
    };

    const activeFilterChips = (
      <>
        {filterType !== "ALL" && (
          <Chip
            label={`Type: ${filterType}`}
            size="small"
            onDelete={() => setFilterType("ALL")}
          />
        )}
        {filterStatus !== "ALL" && (
          <Chip
            label={`Status: ${filterStatus}`}
            size="small"
            onDelete={() => setFilterStatus("ALL")}
          />
        )}
        {filterVisibility !== "ALL" && (
          <Chip
            label={`Visibility: ${filterVisibility}`}
            size="small"
            onDelete={() => setFilterVisibility("ALL")}
          />
        )}
        {filterTags.map((t) => (
          <Chip
            key={t}
            label={`Tag: ${t}`}
            size="small"
            onDelete={() => setFilterTags((prev) => prev.filter((x) => x !== t))}
          />
        ))}
      </>
    );

    return (
      <Box
        sx={{
          p: 3,
          minHeight: "100vh",
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.02
          )} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
        }}
      >
        {/* Enhanced Header Section */}
        <Fade in timeout={600}>
          <GlassCard
            sx={{
              mb: 3,
              p: 3,
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <BusinessIcon
                    sx={{
                      fontSize: 32,
                      color: theme.palette.primary.main,
                    }}
                  />
                  <Typography variant="h4" fontWeight={900}>
                    Accounts
                  </Typography>
                  <Badge
                    badgeContent={total}
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Manage and track all your accounts
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Export (Excel)">
                  <span>
                    <StyledIconButton
                      onClick={exportAccountsExcel}
                      disabled={!rows.length}
                      sx={{
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        "&:hover": {
                          bgcolor: alpha(theme.palette.info.main, 0.2),
                        },
                      }}
                    >
                      <FileDownloadIcon />
                    </StyledIconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Refresh">
                  <StyledIconButton
                    onClick={fetchAccounts}
                    sx={{
                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.secondary.main, 0.2),
                      },
                    }}
                  >
                    <RefreshIcon />
                  </StyledIconButton>
                </Tooltip>
                <StyledButton
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreateDialog}
                  disabled={!workspaceId}
                  sx={{
                    minWidth: 160,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }}
                >
                  New Account
                </StyledButton>
              </Stack>
            </Stack>
            <TextField
              placeholder="Search accounts by name, email, or domain…"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(0);
              }}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchText("");
                        setPage(0);
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
                  },
                },
              }}
            />

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-start"
            >
              <StyledButton
                startIcon={<FilterAltIcon />}
                variant={filtersOpen ? "contained" : "outlined"}
                color={filtersOpen ? "secondary" : "inherit"}
                onClick={() => setFiltersOpen((v) => !v)}
                size="small"
              >
                {filtersOpen ? "Hide Filters" : "Filters"}
                {filtersOpen ? (
                  <ExpandLessIcon sx={{ ml: 0.5 }} />
                ) : (
                  <ExpandMoreIcon sx={{ ml: 0.5 }} />
                )}
              </StyledButton>
            </Stack>

            {/* Active filters quick chips */}
            {(filterType !== "ALL" ||
              filterStatus !== "ALL" ||
              filterVisibility !== "ALL" ||
              filterTags.length > 0) && (
              <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" alignItems="center">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mr: 0.5, fontWeight: 600 }}
                >
                  Active Filters:
                </Typography>
                {activeFilterChips}
                <Button
                  size="small"
                  onClick={resetFilters}
                  sx={{
                    ml: 0.5,
                    borderRadius: 2,
                    textTransform: "none",
                  }}
                  variant="outlined"
                >
                  Clear all
                </Button>
              </Stack>
            )}

            {/* Advanced Filters */}
            <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
              <Divider sx={{ my: 2 }} />
              <GridLike containerSpacing={1.5}>
                <SelectLike
                  label="Type"
                  value={filterType}
                  onChange={(v) => {
                    setFilterType(v);
                    setPage(0);
                  }}
                  options={TYPE_OPTIONS as unknown as string[]}
                />

                <SelectLike
                  label="Status"
                  value={filterStatus}
                  onChange={(v) => {
                    setFilterStatus(v);
                    setPage(0);
                  }}
                  options={STATUS_OPTIONS as unknown as string[]}
                />

                <SelectLike
                  label="Visibility"
                  value={filterVisibility}
                  onChange={(v) => {
                    setFilterVisibility(v);
                    setPage(0);
                  }}
                  options={VISIBILITY_OPTIONS as unknown as string[]}
                />

                <Box sx={{ minWidth: 260, flex: 1 }}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={tagOptions}
                    value={filterTags}
                    onChange={(_, v) => {
                      setFilterTags(v);
                      setPage(0);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tags"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    )}
                  />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={resetFilters}
                    sx={{ borderRadius: 2, textTransform: "none" }}
                  >
                    Reset
                  </Button>
                </Box>
              </GridLike>
            </Collapse>
          </GlassCard>
        </Fade>

        {/* Data table */}
        <GlassCard>
          {loading ? (
            <Box sx={{ p: 3 }}>
              <Stack spacing={2}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Card key={i} elevation={0} sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Skeleton variant="circular" width={48} height={48} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="40%" height={24} />
                          <Skeleton variant="text" width="60%" height={20} />
                        </Box>
                        <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 1 }} />
                        <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 1 }} />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          ) : rows.length === 0 ? (
            <EmptyState>
              <BusinessIcon
                sx={{
                  fontSize: 80,
                  color: theme.palette.text.secondary,
                  mb: 2,
                  opacity: 0.3,
                }}
              />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                No Accounts Found
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
                {searchText || filterType !== "ALL" || filterStatus !== "ALL" || filterVisibility !== "ALL" || filterTags.length > 0
                  ? "Try adjusting your search or filters to find accounts"
                  : "Create your first account to get started"}
              </Typography>
              {(searchText || filterType !== "ALL" || filterStatus !== "ALL" || filterVisibility !== "ALL" || filterTags.length > 0) ? (
                <StyledButton variant="outlined" onClick={resetFilters}>
                  Clear Filters
                </StyledButton>
              ) : (
                <StyledButton
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreateDialog}
                  disabled={!workspaceId}
                >
                  Create Account
                </StyledButton>
              )}
            </EmptyState>
          ) : (
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sortDirection={orderBy === "name" ? order : false}
                      sx={{
                        bgcolor: theme.palette.mode === "dark"
                          ? alpha(theme.palette.background.paper, 0.8)
                          : alpha("#fff", 0.9),
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "name"}
                        direction={orderBy === "name" ? order : "asc"}
                        onClick={() => handleRequestSort("name")}
                      >
                        <Typography variant="subtitle2" fontWeight={700}>
                          Name
                        </Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: theme.palette.mode === "dark"
                          ? alpha(theme.palette.background.paper, 0.8)
                          : alpha("#fff", 0.9),
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>
                        Contact
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: theme.palette.mode === "dark"
                          ? alpha(theme.palette.background.paper, 0.8)
                          : alpha("#fff", 0.9),
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>
                        Classification
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: theme.palette.mode === "dark"
                          ? alpha(theme.palette.background.paper, 0.8)
                          : alpha("#fff", 0.9),
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>
                        Tags
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="right"
                      width="14%"
                      sx={{
                        bgcolor: theme.palette.mode === "dark"
                          ? alpha(theme.palette.background.paper, 0.8)
                          : alpha("#fff", 0.9),
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>
                        Actions
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((a, index) => (
                    <Fade in timeout={300 + index * 50} key={a.id}>
                      <TableRow
                        hover
                        onDoubleClick={() => navigate(`/accounts/${a.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") navigate(`/accounts/${a.id}`);
                        }}
                        role="button"
                        tabIndex={0}
                        sx={{
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            transform: "translateX(4px)",
                          },
                        }}
                      >
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              flexWrap="wrap"
                            >
                              <AccountCircleIcon
                                sx={{
                                  fontSize: 24,
                                  color: theme.palette.primary.main,
                                  opacity: 0.7,
                                }}
                              />
                              <Typography variant="subtitle1" fontWeight={600}>
                                {a.name}
                              </Typography>
                              <Tooltip title="Open details">
                                <StyledIconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/accounts/${a.id}`);
                                  }}
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </StyledIconButton>
                              </Tooltip>
                            </Box>

                          <Typography variant="caption" color="text.secondary">
                            Slug: {a.slug} • Workspace: {a.workspaceId}
                            {a.linkedUserId ? ` • User #${a.linkedUserId}` : ""}
                          </Typography>
                          {a.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                            >
                              {a.description}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.5}>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            flexWrap="wrap"
                          >
                            <Typography variant="body2">
                              {a.email || a.website || a.domain || "—"}
                            </Typography>
                            {(a.email || a.website || a.domain) && (
                              <Tooltip title="Copy">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(
                                      a.email || a.website || a.domain || ""
                                    );
                                  }}
                                >
                                  <ContentCopyIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {a.city ? `${a.city}, ` : ""}
                            {a.country || ""}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip label={a.type} size="small" />
                          <Chip
                            label={a.status}
                            size="small"
                            color={
                              a.status === "ACTIVE"
                                ? "success"
                                : a.status === "PROSPECT"
                                ? "info"
                                : a.status === "ARCHIVED"
                                ? "default"
                                : "warning"
                            }
                          />
                          <Chip
                            label={a.visibility}
                            size="small"
                            variant="outlined"
                          />
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {a.tags?.length
                            ? a.tags
                                .slice(0, 5)
                                .map((t, idx) => (
                                  <Chip
                                    key={t + idx}
                                    label={t}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))
                            : "—"}
                          {a.tags && a.tags.length > 5 && (
                            <Chip
                              label={`+${a.tags.length - 5}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </TableCell>

                        <TableCell
                          align="right"
                          onDoubleClick={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Edit">
                              <StyledIconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditAccount(a);
                                  setEditDialogOpen(true);
                                }}
                                onDoubleClick={(e) => e.stopPropagation()}
                                sx={{
                                  "&:hover": {
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </StyledIconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <StyledIconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteAccountId(a.id);
                                  setDeleteDialogOpen(true);
                                }}
                                onDoubleClick={(e) => e.stopPropagation()}
                                sx={{
                                  "&:hover": {
                                    bgcolor: alpha(theme.palette.error.main, 0.1),
                                  },
                                }}
                              >
                                <DeleteIcon color="error" fontSize="small" />
                              </StyledIconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    </Fade>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && rows.length > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={onPageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={onRowsPerPageChange}
              rowsPerPageOptions={[10, 20, 50, 100]}
              sx={{
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            />
          )}
        </GlassCard>

        {/* Create */}
        <AccountFormDialog
          open={addDialogOpen}
          mode="create"
          initial={null}
          workspaceId={workspaceId}
          onClose={() => setAddDialogOpen(false)}
          onSave={handleCreate}
        />

        {/* Edit */}
        <AccountFormDialog
          open={editDialogOpen}
          mode="edit"
          initial={editAccount}
          onClose={() => setEditDialogOpen(false)}
          onSave={async (payload) => {
            if (!editAccount) return;
            await handleUpdate(editAccount.id, payload);
          }}
        />

        {/* Delete confirm */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              minWidth: 400,
            },
          }}
        >
          <DialogTitle
            sx={{
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              pb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DeleteIcon color="error" />
              <Typography variant="h6" fontWeight={700}>
                Confirm Deletion
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography variant="body1" color="text.secondary">
              Are you sure you want to delete this account? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              p: 2,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Cancel
            </Button>
            <StyledButton
              color="error"
              variant="contained"
              onClick={() => deleteAccountId && handleDelete(deleteAccountId)}
            >
              Delete
            </StyledButton>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{
              width: "100%",
              borderRadius: 2,
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
            }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  };

  // Small layout helpers (nicer spacing in filters row)
  const GridLike: React.FC<{
    containerSpacing?: number;
    children: React.ReactNode;
  }> = ({ containerSpacing = 1, children }) => {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: containerSpacing,
        }}
      >
        {children}
      </Box>
    );
  };

  const SelectLike: React.FC<{
    label: string;
    value: string;
    options: string[];
    onChange: (val: any) => void;
  }> = ({ label, value, options, onChange }) => {
    return (
      <TextField
        select
        size="small"
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{ minWidth: 220 }}
      >
        {options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {opt}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  export default Accounts;
